import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CircleDotIcon, CircleIcon, EllipsisVerticalIcon } from "lucide-react";

import { CampaignSummary } from "@/components/CampaignSummary";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/toast";
import { rejectPitches, setCampaignPitchingOpen, UserContext } from "../../../services/firebase";
import type { Campaign, Client, Pitch } from "../../../shared/types";
import { isPitchReviewer } from "../../../utils/pitching/permissions";
import { campaignNeedsSettingsBeforePitching } from "../../../utils/pitching/requirements";
import { missingFieldsToOpenPitching } from "../../../utils/pitching/requirements";
import { pitchingOpenRequirementsCopy } from "../../../utils/pitching/requirements";
import { pitchesToReject, visiblePitches, type ConfirmKind } from "../campaign";
import { OpenCloseDialog } from "./OpenCloseDialog";
import { RejectRemainingDialog } from "./RejectRemainingDialog";

type CampaignDetailsHeaderProps = {
    campaign: Campaign;
    client?: Client;
    pitches: Pitch[];
};

/** Brand, platforms, Open/Closed badge, and the Settings menu. */
export function CampaignDetailsHeader({ campaign, client, pitches }: CampaignDetailsHeaderProps) {
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const campaignId = campaign.id;
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [rejectOpen, setRejectOpen] = useState(false);
    const [rejectPitched, setRejectPitched] = useState(true);
    const [rejectNeedsInfo, setRejectNeedsInfo] = useState(true);
    const [actionPending, setActionPending] = useState(false);

    const isOpen = Boolean(campaign.pitchingOpen);
    const wasOpened = Boolean(campaign.pitchingOpenChangedAt);
    const reviewer = isPitchReviewer(user, campaign);
    const showRejectRemaining = pitches.some((pitch) => pitch.status === "pitched" || pitch.status === "needs-info");

    function onOpenClick() {
        if (!campaignId) {
            return;
        }
        const firstOpen = !campaign.pitchingOpenChangedAt;
        if (firstOpen && campaignNeedsSettingsBeforePitching(campaign)) {
            const copy = pitchingOpenRequirementsCopy(missingFieldsToOpenPitching(campaign));
            toast.add({
                type: "warning",
                title: copy.title,
                description: copy.description,
            });
            navigate(`/campaign/${campaignId}/settings?open=1`);
            return;
        }
        setConfirmKind("open");
    }

    /** Confirm opening or closing the campaign to pitches. */
    async function confirmOpenClose() {
        if (!confirmKind) {
            return;
        }
        setActionPending(true);
        try {
            if (campaignId) {
                const result = await setCampaignPitchingOpen(campaignId, confirmKind === "open");
                if (!result.success) {
                    console.error(result.error);
                    toast.add({
                        type: "error",
                        title: "Couldn’t update this campaign",
                        description: "Refresh and try again.",
                    });
                }
            }
            setConfirmKind(null);
        } finally {
            setActionPending(false);
        }
    }

    async function confirmRejectRemaining() {
        const targets = pitchesToReject(
            visiblePitches(user, campaign, pitches),
            rejectPitched,
            rejectNeedsInfo,
        );
        if (targets.length === 0) {
            toast.add({
                type: "error",
                title: "No pitches to reject",
                description: "Select Pitched and/or Needs info.",
            });
            return;
        }

        setActionPending(true);
        try {
            const result = await rejectPitches(targets.map((pitch) => pitch.id));
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: "Couldn’t reject some pitches",
                    description: "Refresh and try again.",
                });
                return;
            }
            setRejectOpen(false);
        } finally {
            setActionPending(false);
        }
    }

    const statusAndSettings = (
        <div className="flex shrink-0 items-center gap-3">
            {isOpen ? (
                <Badge variant="success">
                    <CircleDotIcon />
                    Open
                </Badge>
            ) : wasOpened ? (
                <Badge variant="destructive">
                    <CircleIcon className="fill-current" />
                    Closed
                </Badge>
            ) : null}

            {reviewer && (
                <DropdownMenu>
                    <DropdownMenuTrigger render={<Button type="button" variant="ghost" size="xs" />}>
                        Settings
                        <EllipsisVerticalIcon data-icon="inline-end" />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-auto min-w-56">
                        <DropdownMenuItem onClick={() => navigate(`/campaign/${campaignId}/settings`)}>
                            Edit Settings
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={isOpen ? () => setConfirmKind("close") : onOpenClick}>
                            {isOpen ? "Close to new pitches" : "Open for pitches"}
                        </DropdownMenuItem>
                        {showRejectRemaining ? (
                            <DropdownMenuItem variant="destructive" onClick={() => setRejectOpen(true)}>
                                Reject remaining pitches
                            </DropdownMenuItem>
                        ) : null}
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </div>
    );

    return (
        <>
            <CampaignSummary
                campaign={campaign}
                company={client?.name}
                companySector={client?.companySector}
                statusAndSettings={statusAndSettings}
            />
            <OpenCloseDialog
                confirmKind={confirmKind}
                onOpenChange={(open) => {
                    if (!open) {
                        setConfirmKind(null);
                    }
                }}
                actionPending={actionPending}
                onConfirm={() => void confirmOpenClose()}
                onCancel={() => setConfirmKind(null)}
            />
            <RejectRemainingDialog
                open={rejectOpen}
                onOpenChange={(open) => {
                    if (!open) {
                        setRejectOpen(false);
                        setRejectPitched(true);
                        setRejectNeedsInfo(true);
                    }
                }}
                rejectPitched={rejectPitched}
                onRejectPitchedChange={setRejectPitched}
                rejectNeedsInfo={rejectNeedsInfo}
                onRejectNeedsInfoChange={setRejectNeedsInfo}
                actionPending={actionPending}
                onConfirm={() => void confirmRejectRemaining()}
                onCancel={() => setRejectOpen(false)}
            />
        </>
    );
}
